/** „Dostępność → Ludzie” (spec 2026-10-25): what the people × days grid shows, without React. */
import { confirmationLines, type Confirmation } from '../crew/confirmation';

export const PEOPLE_AVAILABILITY_RESOURCE = 'crew.people_availability';
export const PEOPLE_GRID_KEY = ['crew', 'people-availability'] as const;

/** `active` false: another event took this day (stage 1b D); `collision`: two active events overlap that day (C). */
/** `multiDay`: the person works several days of that event (owner 2026-10-26: „Usuń ze stanowiska” asks which). */
/** `confirmation` (owner 2026-10-28 12:01): how a „Potwierdzony” was confirmed — the P-tile's bubble. */
export type GridAssignment = { assignmentId?: string; eventId?: string; eventName: string; functionName?: string; status?: string; active?: boolean; collision?: boolean; datesChanged?: boolean; multiDay?: boolean; confirmation?: Confirmation };
export type GridQuestion = { eventId?: string; eventName: string; sentAt?: string };
/** One non-empty cell as the API sends it: fields the role may not see are absent (rule 7a). */
export type GridCell = {
  personId: string;
  day: string;
  assignments?: GridAssignment[];
  questions?: GridQuestion[];
  collision?: boolean;
  /**
   * Stage 2 (Q1): „tak” to every day of a question about an event, but not in its crew — „potwierdził — przydziel ręcznie”;
   * `questionId` (owner 2026-10-29 00:32, only with the event named): for „Zrezygnuj z przydziału”.
   */
  toAssign?: Array<{ eventId?: string; eventName: string; reason: string; questionId?: string }>;
  /** Q3: working though marked unavailable — red. */
  unavailableConflict?: boolean;
  state?: 'available' | 'unavailable';
  /** Stage 1b A: the mark's hours (absent = the whole day). */
  startsAt?: string;
  endsAt?: string;
  source?: string;
  setBy?: string;
  setAt?: string;
};
/** `name` absent: hidden by the setting „nazwiska tylko z prawem Osoby: Podgląd” (K). */
/** 16:52 / 16:53: an event deleted or cancelled released the person — „powiadom tę osobę” until „Odhacz”. */
export type ReleaseNotice = { id: string; personId: string; personName?: string; eventName: string; days: string; kind: 'deleted' | 'cancelled' };
/** `name` absent (K): `functionLabel` — the person's function(s) instead (owner's Q4). */
/** `workDays` (owner 2026-10-26 07:49, the „dni” column): the days of the range the person works, active days only. */
export type Grid = { days: string[]; people: Array<{ id: string; name?: string; functionLabel?: string; workDays?: number }>; cells: GridCell[]; notices?: ReleaseNotice[] };
/** A person's label in the grid: the name, else their function (Q4). */
export const personLabel = (p: { name?: string; functionLabel?: string }) => p.name ?? p.functionLabel ?? 'Osoba';
/** „Jan Kowalski: wydarzenie „Wesele” (7.11.2026) usunięte — powiadom tę osobę”. */
export const noticeText = (n: ReleaseNotice, personName: string) => `${personName}: wydarzenie „${n.eventName}” (${n.days}) ${n.kind === 'deleted' ? 'usunięte' : 'odwołane'} — powiadom tę osobę`;

/**
 * Owner 2026-10-26 07:42: „Usuń ze stanowiska” of an assignment cell — the question:
 * „Usunąć Jan Kowalski ze stanowiska Technik w „Wesele” (7.11.2026)?”.
 */
export const releaseQuestion = (personName: string, a: Pick<GridAssignment, 'eventName' | 'functionName'>, day: string): string =>
  `Usunąć ${personName} ze stanowiska${a.functionName ? ` ${a.functionName}` : ''} w „${a.eventName}” (${shortDate(day)})?`;

/** After it: „Jan Kowalski: usunięto ze stanowiska (7.11.2026) — miejsce jest wolne, do obsadzenia.” */
export const releasedText = (personName: string, days: readonly string[]): string =>
  `${personName}: usunięto ze stanowiska (${days.map(shortDate).join(', ')}) — miejsce jest wolne, do obsadzenia.`;

/** Stage 1b K: the settings ⚙ of „Dostępność → Ludzie”. */
/** `missingPastDays` (owner 2026-10-27 12:26): the „Brakuje” row shows the days before today too. */
export type PeopleSettings = { unstaffedEnds: 'filled' | 'filled_or_manual'; withdrawalEmail: boolean; cancelledCount: boolean; namesNeedPersonsView: boolean; questionCountsAnswered: boolean; missingPastDays: boolean; version: number };
export const PEOPLE_SETTINGS_KEY = ['crew', 'people-settings'] as const;

/** Stage 1b I: one entry of a person-day's history. */
/** `consulted` (owner 2026-10-29 00:29): the person's own mark changed after consulting them. */
export type HistoryEntry = { action: string; label: string; eventId?: string; eventName?: string; functionName?: string; because?: { eventId?: string; eventName: string }; state?: string; hours?: string; fromRefusal?: boolean; copy?: boolean; fromQuestion?: boolean; keptManual?: boolean; reason?: string; consulted?: boolean; actor?: string; at?: string };

/** The words after a mark changed after consulting the person (owner 2026-10-29 00:29). */
export const CONSULTED_NOTE = 'wpis pracownika zmieniony po konsultacji z nim';

const TIME = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
/** '10:00–14:00' of a mark's hours. */
export const markHours = (cell: Pick<GridCell, 'startsAt' | 'endsAt'>): string => (cell.startsAt && cell.endsAt ? `${TIME.format(new Date(cell.startsAt))}–${TIME.format(new Date(cell.endsAt))}` : '');

/** One line of the history, in Polish: „7.11.2026 14:05 · Szef — wymuszono: Wesele B (Realizator FOH)”. */
export function historyText(e: HistoryEntry): string {
  const when = e.at ? `${shortDate(e.at)} ${TIME.format(new Date(e.at))}` : '';
  const who = e.actor !== undefined ? (e.actor === 'program' ? 'program' : e.actor || '—') : '';
  const head = [when, who].filter(Boolean).join(' · ');
  const consulted = e.consulted ? ` — ${CONSULTED_NOTE}` : '';
  const what =
    e.action === 'availability_set'
      ? `${e.state === 'available' ? 'Dostępny' : 'Niedostępny'}${e.hours ? ` ${e.hours}` : ''}${e.reason === 'odmowa' ? ` — „Odmowa” w obsadzie: ${e.eventName ?? 'inne wydarzenie'}` : ''}${consulted}`
      : e.action === 'availability_cleared'
        ? `wyczyszczono dostępność${consulted}`
        : e.action === 'availability_restored'
          ? `${e.label} (${e.eventName ?? 'inne wydarzenie'}): ${e.state ? `${e.state === 'available' ? 'Dostępny' : 'Niedostępny'}${e.hours ? ` ${e.hours}` : ''}` : 'bez oznaczenia'}`
          : e.action === 'question_answered'
            ? `odpowiedź na zapytanie: ${e.state === 'available' ? 'tak' : e.state === 'unavailable' ? 'nie' : '—'} (${e.eventName ?? 'zapytanie o termin'})${e.keptManual ? ' — zostaje oznaczenie ręczne (nowsze niż zapytanie)' : ''}`
            : `${e.label}: ${e.eventName ?? 'inne wydarzenie'}${e.functionName ? ` (${e.functionName})` : ''}${e.because ? (e.action === 'reactivated' ? ` — „${e.because.eventName}” zwolniło dzień` : ` — przez „${e.because.eventName}”`) : ''}${e.fromRefusal ? ' — z „Odmowa”' : ''}${e.copy ? ' — kopia w serii' : ''}${e.fromQuestion ? ' — po odpowiedzi „tak” (dostępność potwierdzona)' : ''}`;
  return head ? `${head} — ${what}` : what;
}

const pad = (n: number) => String(n).padStart(2, '0');
/** 'YYYY-MM' of a day. */
export const monthOf = (day: string) => day.slice(0, 7);
/** The first and last day of a month ('YYYY-MM'). */
export function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${pad(last)}` };
}
/** The month `delta` months later (earlier when negative). */
export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}
/** Every day of the month. */
export function monthDays(month: string): string[] {
  const { to } = monthRange(month);
  return Array.from({ length: Number(to.slice(8)) }, (_, i) => `${month}-${pad(i + 1)}`);
}

/** '2026-11-07' (or an instant) → '7.11.2026'. */
export const shortDate = (value: string) => {
  const day = value.length > 10 ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' }).format(new Date(value)) : value;
  return `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}.${day.slice(0, 4)}`;
};

const SOURCES: Record<string, string> = { manual: 'ręcznie', answer: 'odpowiedź na zapytanie', self: 'sama osoba' };

/**
 * Owner 2026-10-26 07:49: one set of statuses for a cell — confirmed (filled), proposed (outline), asked (blue),
 * collision (red with a border), available (green), unavailable (hatched), inactive (grey); empty.
 */
export type CellKind = 'confirmed' | 'proposed' | 'asked' | 'collision' | 'available' | 'unavailable' | 'inactive' | 'empty';
/**
 * `marker` (owner 2026-10-27 08:14, 12:32): one letter in the middle of the square tile — D (dostępny), N (niedostępny),
 * Z (zapytany: a question waiting for its answer), P (potwierdzony).
 */
export type CellView = { kind: CellKind; label: string; title: string; assignments: GridAssignment[]; marker?: true };

/** The legend under the grid: every letter and colour (owner 12:32), then the tiles with an event's name. */
export const LEGEND: ReadonlyArray<{ kind: Exclude<CellKind, 'empty'>; label: string; letter?: string }> = [
  { kind: 'available', label: 'dostępny', letter: 'D' },
  { kind: 'unavailable', label: 'niedostępny', letter: 'N' },
  { kind: 'asked', label: 'zapytany', letter: 'Z' },
  { kind: 'confirmed', label: 'potwierdzony', letter: 'P' },
  { kind: 'proposed', label: 'proponowany' },
  { kind: 'collision', label: 'kolizja' },
  { kind: 'inactive', label: 'nieaktywny' },
];
/** Owner 2026-10-27 23:31: the menu line of a past day (rule B — no staffing, no questions there). */
export const PAST_DAY_NOTE = 'Dzień minął — nie można obsadzać ani wysyłać zapytań';

/** '…T11:40:00Z' → '27.10 12:40' (Warsaw). */
const dayTime = (iso: string) => `${dayMonth(new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' }).format(new Date(iso)))} ${TIME.format(new Date(iso))}`;

/**
 * What a cell shows, by priority: an assignment (collision — two active events or working while unavailable; only
 * inactive ones — inactive; else by the active one: „Potwierdzony” — confirmed, a question about its event without an
 * answer — asked, else proposed), then unavailable, available (also „przydziel ręcznie”), a question, nothing.
 */
export function cellView(cell: GridCell | undefined): CellView {
  if (!cell) return { kind: 'empty', label: '', title: '', assignments: [] };
  const hours = markHours(cell);
  const mark = cell.state
    ? `${cell.state === 'available' ? 'Dostępny' : 'Niedostępny'}${hours ? ` ${hours}` : ''}${cell.setBy ? ` — ustawił(a): ${cell.setBy}` : ''}${cell.source ? ` (${SOURCES[cell.source] ?? cell.source})` : ''}${cell.setAt ? `, ${shortDate(cell.setAt)}` : ''}`
    : '';
  const asked = (cell.questions ?? []).map((q) => `Zapytanie bez odpowiedzi: ${q.eventName}${q.sentAt ? ` (wysłane ${shortDate(q.sentAt)})` : ''}`);
  const list = cell.assignments ?? [];
  if (list.length > 0) {
    const what = list.map((a) => `${a.eventName}${a.functionName ? ` — ${a.functionName}` : ''}${a.active === false ? ' (nieaktywny)' : ''}${a.datesChanged ? ' — zmieniono datę, zapytaj ponownie' : ''}`);
    const warn = [...(cell.collision ? ['Kolizja: dwa wydarzenia tego dnia — wybierz, które robi'] : []), ...(cell.unavailableConflict ? ['Niedostępny, a jest w obsadzie'] : [])];
    const title = [...warn, ...what, mark, ...asked].filter(Boolean).join('\n');
    const active = list.filter((a) => a.active !== false);
    if (cell.collision === true || cell.unavailableConflict === true) {
      const names = (cell.collision ? list.filter((a) => a.collision) : active).map((a) => a.eventName);
      return { kind: 'collision', label: (names.length > 0 ? names : list.map((a) => a.eventName)).join(' + '), title, assignments: list };
    }
    if (active.length === 0) return { kind: 'inactive', label: list[0]!.eventName, title, assignments: list };
    const main = active[0]!;
    // owner 12:32: „Potwierdzony” — the letter P (the event and function in the title); rule 7a: no status, no letter;
    // owner 2026-10-28 12:01: the bubble says how it was confirmed (the ⓘ of the event panel says the same)
    if (main.status === 'potwierdzony') {
      const how = confirmationLines(main.confirmation);
      return { kind: 'confirmed', label: 'P', marker: true, title: [...warn, ...what, ...how, mark, ...asked].filter(Boolean).join('\n'), assignments: list };
    }
    const kind: CellKind = main.eventId && (cell.questions ?? []).some((q) => q.eventId === main.eventId) ? 'asked' : 'proposed';
    return { kind, label: main.eventName, title, assignments: list };
  }
  // stage 2 (Q1): confirmed every day, not in the crew — the owner assigns by hand
  const toAssign = (cell.toAssign ?? []).map((t) => `${t.eventName}: ${t.reason}`);
  if (toAssign.length > 0) return { kind: 'available', label: 'przydziel ręcznie', title: [...toAssign, mark, ...asked].filter(Boolean).join('\n'), assignments: [] };
  // owner 2026-10-27 08:14: just the letter on the same colour (the hours and who set it in the title)
  if (cell.state) return { kind: cell.state, label: cell.state === 'available' ? 'D' : 'N', marker: true, title: [mark, ...asked].join('\n'), assignments: [] };
  // owner 12:32: Z — asked, waiting for the answer; the title says when („Zapytanie wysłane 27.10 12:40 (Wesele A)”)
  if (asked.length > 0) return { kind: 'asked', label: 'Z', marker: true, title: (cell.questions ?? []).map((q) => `Zapytanie wysłane${q.sentAt ? ` ${dayTime(q.sentAt)}` : ''} (${q.eventName})`).join('\n'), assignments: [] };
  return { kind: 'empty', label: '', title: '', assignments: [] };
}

/** What the user may do in the cell menu (owner 2026-10-26 07:49): their rights, as the page knows them. */
/** `confirm` (owner 2026-10-28 12:01 „Potwierdź ręcznie”): „Obsada: Edycja” with the field „Status”. */
export type MenuRights = { staff: boolean; edit: boolean; force: boolean; ask: boolean; release?: boolean; confirm?: boolean };
/** `dismiss` (owner 2026-10-29 00:32): „Zrezygnuj z przydziału” of the question `questionId`. */
export type CellAction = { id: 'fill' | 'dismiss' | 'confirm' | 'release' | 'force' | 'available' | 'unavailable' | 'clear' | 'ask' | 'history'; label: string; assignmentId?: string; questionId?: string };

/**
 * The one menu of a person's cell: Obsadź…, Usuń ze stanowiska (owner 07:42; per event of the cell the user may see),
 * Wymuś tutaj, Dostępny / Niedostępny (Wyczyść on a marked day), Wyślij zapytanie, Historia — only what applies to the
 * cell and the user may do. Days before today: only the marks and the history (nothing changes in the past).
 */
export function cellActions(cell: GridCell | undefined, day: string, today: string, rights: MenuRights): CellAction[] {
  const list = cell?.assignments ?? [];
  const working = list.some((a) => a.active !== false);
  const future = day >= today;
  const out: CellAction[] = [];
  // owner 2026-10-27 12:37: „Obsadź” on a day „Dostępny” or „Z” (staffed at once — review M6), „Niedostępny” for some hours
  // only (review M7: the fill refuses when they meet the work), or with no mark (asked first — the right to ask too)
  const wholeDayOff = cell?.state === 'unavailable' && !cell.startsAt;
  const mayQuick = !working && !wholeDayOff && (!!cell?.state || (cell?.questions ?? []).length > 0 || (cell?.toAssign ?? []).length > 0 || rights.ask);
  if (future && rights.staff && mayQuick) out.push({ id: 'fill', label: 'Obsadź' });
  // owner 2026-10-29 00:32: „przydziel ręcznie” — I changed my mind (the rights of „Obsadź”); per event the user may see
  if (rights.staff) {
    const waiting = (cell?.toAssign ?? []).filter((t) => t.questionId);
    for (const t of waiting) out.push({ id: 'dismiss', label: waiting.length > 1 ? `Zrezygnuj z przydziału: ${t.eventName}` : 'Zrezygnuj z przydziału', questionId: t.questionId! });
  }
  // owner 2026-10-28 12:01 „Potwierdź ręcznie”: a „Proponowany” (also „Z” — asked, with a place) active that day, one per
  // event the user may see; rule B — not on a past day
  if (future && rights.confirm) {
    const open = list.filter((a) => a.assignmentId && a.eventId && a.active !== false && a.status === 'proponowany');
    for (const a of open) out.push({ id: 'confirm', label: open.length > 1 ? `Potwierdź ręcznie: ${a.eventName}` : 'Potwierdź ręcznie', assignmentId: a.assignmentId! });
  }
  if (future && rights.release) {
    // review M4: not an assignment inactive that day (the person does not work there that day)
    const placed = list.filter((a) => a.assignmentId && a.eventId && a.active !== false);
    for (const a of placed) out.push({ id: 'release', label: placed.length > 1 ? `Usuń ze stanowiska: ${a.eventName}` : 'Usuń ze stanowiska', assignmentId: a.assignmentId! });
  }
  if (future && rights.force) {
    for (const a of list) {
      if (!a.assignmentId || !a.eventId) continue;
      if ((cell?.collision && a.collision) || a.active === false) out.push({ id: 'force', label: `Wymuś tutaj: ${a.eventName}`, assignmentId: a.assignmentId });
    }
  }
  if (rights.edit) {
    if (cell?.state !== 'available') out.push({ id: 'available', label: 'Dostępny' });
    if (cell?.state !== 'unavailable') out.push({ id: 'unavailable', label: 'Niedostępny' });
    if (cell?.state) out.push({ id: 'clear', label: 'Wyczyść' });
  }
  if (future && rights.ask && !working) out.push({ id: 'ask', label: 'Wyślij zapytanie' });
  out.push({ id: 'history', label: 'Historia' });
  return out;
}

/** Owner 2026-10-27 08:14: „5 komórek”, „2 komórki”, „1 komórka”. */
export function cellsCount(n: number): string {
  const tens = n % 100;
  const ones = n % 10;
  const word = n === 1 ? 'komórka' : ones >= 2 && ones <= 4 && (tens < 12 || tens > 14) ? 'komórki' : 'komórek';
  return `${n} ${word}`;
}

/**
 * Owner 2026-10-27 08:14: the menu of a cell in a selection of several — its actions apply to every selected cell:
 * „Obsadź…”, „Dostępny”, „Niedostępny”, each with the count, e.g. „Niedostępny (5 komórek)”.
 */
export function multiActions(rights: MenuRights, n: number): CellAction[] {
  const count = ` (${cellsCount(n)})`;
  return [
    ...(rights.staff ? [{ id: 'fill' as const, label: `Obsadź${count}` }] : []),
    ...(rights.confirm ? [{ id: 'confirm' as const, label: `Potwierdź ręcznie${count}` }] : []),
    ...(rights.edit ? [{ id: 'available' as const, label: `Dostępny${count}` }, { id: 'unavailable' as const, label: `Niedostępny${count}` }] : []),
  ];
}

/** '2026-11-07' → '7.11'. */
export const dayMonth = (day: string) => `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}`;

type Named = { eventId?: string; eventName: string };
/** One cell's result of an action on several cells (POST /api/crew/people-availability/batch). */
/** `released` (review I2): the person was taken off their place that day („Tak”). */
export type BatchResult = { personId: string; day: string; result: 'done' | 'skipped'; reason?: string; message?: string; released?: true; events?: Named[] };
/** `mayRelease` (review M6): whether the user may take the person off that event's place. */
export type BatchQuestion = { personId: string; day: string; events: Named[]; mayRelease?: boolean };

const names = (events: readonly Named[] | undefined) => (events ?? []).map((e) => e.eventName).join(', ');

/** The cells skipped, one line each: „Jan Kowalski 7.11 — już w obsadzie: Wesele A”. */
export function batchLines(results: readonly BatchResult[], nameOf: (personId: string) => string): string[] {
  return results
    .filter((r) => r.result === 'skipped')
    .map((r) => {
      const why =
        r.reason === 'collision'
          ? `już w obsadzie: ${names(r.events)}`
          : r.reason === 'staffed'
            ? `w obsadzie: ${names(r.events)} (bez zmian)`
            : r.reason === 'forbidden'
              ? `brak prawa, by usunąć z obsady: ${names(r.events)} (bez zmian)`
              : r.reason === 'already_confirmed'
                ? 'już potwierdzony'
              : r.reason === 'not_staffed'
                ? 'nie jest tego dnia w obsadzie do potwierdzenia'
              : r.reason === 'several_events'
                ? `kilka wydarzeń (${names(r.events)}) — wybierz w menu kafelka`
              : r.reason === 'not_allowed'
                ? `brak prawa do potwierdzenia: ${names(r.events)}`
              : r.reason === 'not_in_event'
                ? 'poza dniami wydarzenia'
                : r.reason === 'past_day'
                  ? 'dzień minął'
                  : (r.message ?? 'pominięto');
      return `${nameOf(r.personId)} ${dayMonth(r.day)} — ${why}`;
    });
}

/** Review I2: the cells „Tak” took off their places — „Jan Kowalski 7.11”. */
export const releasedLines = (results: readonly BatchResult[], nameOf: (personId: string) => string): string[] => results.filter((r) => r.released).map((r) => `${nameOf(r.personId)} ${dayMonth(r.day)}`);

/** „Wyrzucić z obsady w dniach: …?” — one line per person and day: „Jan Kowalski 7.11 — Wesele A”. */
export const questionLines = (question: readonly BatchQuestion[], nameOf: (personId: string) => string): string[] => question.map((q) => `${nameOf(q.personId)} ${dayMonth(q.day)} — ${names(q.events)}`);

export const MONTHS_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'] as const;
/** The day `n` days later (earlier when negative). */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
/** The Monday of the day's week. */
export const weekStart = (day: string): string => addDays(day, -((new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7));
/** Owner 2026-10-26 07:49: the range „2 tygodnie” from its first day. */
export const twoWeeks = (from: string): { from: string; to: string } => ({ from, to: addDays(from, 13) });
/** „26 paź – 8 lis 2026”, „2 – 15 lis 2026”, „28 gru 2026 – 10 sty 2027”. */
export function rangeTitle(from: string, to: string): string {
  const d = (day: string) => Number(day.slice(8));
  const m = (day: string) => MONTHS_SHORT[Number(day.slice(5, 7)) - 1]!;
  if (from.slice(0, 4) !== to.slice(0, 4)) return `${d(from)} ${m(from)} ${from.slice(0, 4)} – ${d(to)} ${m(to)} ${to.slice(0, 4)}`;
  if (from.slice(0, 7) !== to.slice(0, 7)) return `${d(from)} ${m(from)} – ${d(to)} ${m(to)} ${to.slice(0, 4)}`;
  return `${d(from)} – ${d(to)} ${m(to)} ${to.slice(0, 4)}`;
}
/** Every day from `from` to `to`. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

/** Shift-click in a row: the days from the last clicked one to this one, either way. */
export function rowRange(days: readonly string[], from: string, to: string): string[] {
  const [a, b] = from <= to ? [from, to] : [to, from];
  return days.filter((d) => d >= a && d <= b);
}

/** A selection key: one person on one day. */
export const cellKey = (personId: string, day: string) => `${personId}:${day}`;
/** The selection as the body's `cells`, by person and day. */
export function selectionBody(selected: ReadonlySet<string>): Array<{ personId: string; day: string }> {
  return [...selected]
    .sort()
    .map((key) => {
      const at = key.lastIndexOf(':');
      return { personId: key.slice(0, at), day: key.slice(at + 1) };
    });
}

/**
 * Owner 2026-10-29 00:29: one of the person's own marks („Mój kalendarz”) a change from the grid would overwrite — as the
 * server lists them in 422 `self_mark` (rule 7a: the state, hours, name only when the user may see them). Owner
 * 2026-10-30 01:28: `origin` 'answer' — the mark comes from the person's answer to a question (absent: 'self').
 */
export type SelfMarkDetail = { personId: string; personName?: string; day: string; state?: 'available' | 'unavailable'; hours?: string; setAt?: string; origin?: 'self' | 'answer' };

/**
 * A refusal `self_mark`: its marks and the token of what was listed (review I1 — „Tak, zmień” sends it back as
 * `consulted`); null — not such a refusal.
 */
export function selfMarkAsk(error: unknown): { marks: SelfMarkDetail[]; token: string } | null {
  const e = error as { code?: string; details?: { marks?: unknown; token?: unknown } } | null;
  return e?.code === 'self_mark' && Array.isArray(e.details?.marks) && typeof e.details.token === 'string' ? { marks: e.details.marks as SelfMarkDetail[], token: e.details.token } : null;
}

/**
 * The words of „Wpis pracownika”: one mark — „Ten wpis (Niedostępny 7.11) ustawił sam pracownik w swoim kalendarzu.”
 * or, from an answer, „… pochodzi z odpowiedzi pracownika na zapytanie.”; several — the lead and a line per person and
 * day („Jan Kowalski — Niedostępny 7.11”), each with its origin when both kinds are listed; then the question.
 */
export function selfMarkQuestion(marks: readonly SelfMarkDetail[], nameOf: (personId: string) => string): { lead: string; lines: string[]; ask: string } {
  const what = (m: SelfMarkDetail) => `${m.state ? `${m.state === 'available' ? 'Dostępny' : 'Niedostępny'}${m.hours ? ` ${m.hours}` : ''}` : 'wpis'} ${dayMonth(m.day)}`;
  const one = new Set(marks.map((m) => m.personId)).size <= 1;
  const ask = one ? 'Czy zmiana została skonsultowana z pracownikiem?' : 'Czy zmiana została skonsultowana z pracownikami?';
  const origins = new Set(marks.map((m) => m.origin ?? 'self'));
  const mixed = origins.size > 1;
  const answers = !mixed && origins.has('answer');
  if (marks.length === 1) return { lead: `Ten wpis (${what(marks[0]!)}) ${answers ? 'pochodzi z odpowiedzi pracownika na zapytanie' : 'ustawił sam pracownik w swoim kalendarzu'}.`, lines: [], ask };
  const lead = mixed
    ? one
      ? 'Te wpisy pochodzą od pracownika:'
      : 'Te wpisy pochodzą od pracowników:'
    : answers
      ? one
        ? 'Te wpisy pochodzą z odpowiedzi pracownika na zapytanie:'
        : 'Te wpisy pochodzą z odpowiedzi pracowników na zapytania:'
      : one
        ? 'Te wpisy ustawił sam pracownik w swoim kalendarzu:'
        : 'Te wpisy ustawili sami pracownicy w swoich kalendarzach:';
  const from = (m: SelfMarkDetail) => (!mixed ? '' : (m.origin ?? 'self') === 'answer' ? ' (odpowiedź na zapytanie)' : ' (ustawił w swoim kalendarzu)');
  return { lead, lines: marks.map((m) => `${m.personName ?? nameOf(m.personId)} — ${what(m)}${from(m)}`), ask };
}
